import { Link } from "react-router-dom";
import { buildTenantRelativePath } from "../../utils/tenantPreview";
import styles from "./AdminSettingsTabs.module.css";
import shell from "./AdminShell.module.css";

// Registry of the 17 settings destinations. `key`/`label`/`to` are the
// pre-existing contract (deep-link keys, TAB_ALIASES targets, and
// aria-labels all key off `label` unchanged — see normalizeAdminSettingsTabKey
// below). `group`/`icon`/`description` are new: transcribed verbatim from
// the prototype's own Settings registry (SETTINGS array,
// 06-settings-shell-identity-access-content.jsx:12-40 and
// 07-settings-channels-developers-site.jsx:11-278), matching the same
// group→icon→description triples already used for this exact key by
// Settings.jsx's SETTINGS_HUB_GROUPS (its hub cards link to these same
// keys). `icon` values are AdminShell.module.css class names (same decoded
// icomoon codepoints the hub cards already use — no new glyphs needed
// except the back-link chevron, added locally below).
//
// Two deliberate exceptions, both pre-existing and out of scope to fix
// here (see <SCRATCH>/reports/D-settings-reconciliation.md §1):
//  - `siteOffline`'s label "API and Keys" reads oddly next to its
//    prototype-sourced description "Take the clan offline safely" (the
//    hub card for this key is titled "Maintenance" instead) — a
//    label/hub-card mismatch that predates this change.
//  - `events`'s description is NOT its hub entrypoint's ("Domains": "Custom
//    domains and SSL") but this tab's own existing in-panel heading copy
//    (Settings.jsx's "Kits" section), because this one tab bundles two kit
//    cards (Calendar & Events + Domains) and the Domains-only copy would
//    misrepresent it.
// `emailNotifications` has no prototype/hub source at all (it's a live-only
// tab — see reconciliation report row 17); its description is this tab's
// own existing live heading copy, and it gets its own singleton group (see
// getGroupSiblings below for why that still renders a one-item strip).
export const ADMIN_SETTINGS_TABS = [
  // Identity
  { key: "general", label: "General", group: "Identity", icon: "iconBuilding", description: "Name, contact, sharing and language" },
  { key: "appearance", label: "Appearance", group: "Identity", icon: "iconPaintRoller", description: "Logo, colours and custom CSS" },
  // Access
  { key: "roles", label: "Roles", group: "Access", icon: "iconShieldAlt", description: "Presets and per-permission control" },
  { key: "subclans", label: "Subclans", group: "Access", icon: "iconBuilding", description: "Landing subclan and who builds structure" },
  // Content — same order as the prototype's Content group (cards, gadgets, automod, announce)
  { key: "categories", label: "Cards and Stacks", group: "Content", icon: "iconClipboardCheck", description: "The card and stack types in play" },
  { key: "features", label: "Gadgets", group: "Content", icon: "iconDesktop", description: "Wall, workspace and map gadgets" },
  { key: "censorship", label: "Moderation", group: "Content", icon: "iconShieldAlt", description: "Screen content before it lands" },
  { key: "announcement", label: "Tools", group: "Content", icon: "iconBolt", description: "A single site-wide banner" },
  // Channels
  { key: "smtp", label: "Email", group: "Channels", icon: "iconEnvelope", description: "Outgoing SMTP credentials" },
  { key: "twilio", label: "SMS", group: "Channels", icon: "iconMobileAlt", description: "Twilio for texts and 2FA codes" },
  { key: "linkages", label: "Linkages", group: "Channels", icon: "iconPlug", description: "Route events to and from other tools" },
  // Developers — same order as the prototype's Developers group (apps, maintenance)
  { key: "firebase", label: "Apps", group: "Developers", icon: "iconCode", description: "Registered apps, secrets and webhooks" },
  { key: "siteOffline", label: "API and Keys", group: "Developers", icon: "iconLock", description: "Take the clan offline safely" },
  // Public site — same order as the prototype's Public site group (menu, pages, domains)
  { key: "menu-builder", label: "Menu Builder", to: "/administrator/menu-builder", group: "Public site", icon: "iconGripLines", description: "Your public site's main menu" },
  { key: "custom-pages", label: "Custom Pages", to: "/administrator/pages", group: "Public site", icon: "iconBookOpen", description: "System and custom pages" },
  { key: "events", label: "Kits", group: "Public site", icon: "iconGlobe", description: "Kits are clan-level capabilities that bundle related setup into focused controls." },
  // Live-only — no prototype/hub group at all (see registry comment above)
  { key: "emailNotifications", label: "Notifications", group: "Notifications", icon: "navIconNotifications", description: "Manage email and SMS notification behavior from one place." },
];

const INTERNAL_TAB_KEYS = new Set(
  ADMIN_SETTINGS_TABS.filter((tab) => !tab.to).map((tab) => tab.key)
);

const TAB_ALIASES = {
  subclanCategories: "categories",
  branding: "appearance",
  smsNotifications: "emailNotifications",
  cloudflare: "linkages",
  wall: "features",
  domains: "events",
};

export function getAdminSettingsTabId(key) {
  return `admin-settings-tab-${key}`;
}

export function getAdminSettingsTabPanelId(key) {
  return `admin-settings-panel-${key}`;
}

export function normalizeAdminSettingsTabKey(value) {
  const rawValue = `${value || ""}`.trim();
  if (!rawValue) return null;

  const normalizedValue = TAB_ALIASES[rawValue] || rawValue;
  return INTERNAL_TAB_KEYS.has(normalizedValue) ? normalizedValue : null;
}

function getTabHref(tab, buildHref) {
  if (tab.to) {
    return buildHref(tab.to);
  }

  return buildHref(
    `/administrator/settings?tab=${encodeURIComponent(tab.key)}`
  );
}

function getTabMeta(key) {
  return ADMIN_SETTINGS_TABS.find((tab) => tab.key === key) || null;
}

// Siblings = every registry entry sharing the active tab's `group`,
// including the active tab itself — same shape as the prototype's
// `meta.siblings` (06-…jsx:43, `SET_INDEX[it.k] = {..., siblings: g.items}`).
// Every real prototype group has 2-4 items, so this is never a length-1
// array for the 16 keys backed by the prototype/hub registry.
// `emailNotifications` is the one exception (no prototype/hub group at
// all, see the registry comment above) — it always resolves to a
// length-1 array.
function getGroupSiblings(key) {
  const meta = getTabMeta(key);
  if (!meta) return [];
  return ADMIN_SETTINGS_TABS.filter((tab) => tab.group === meta.group);
}

export default function AdminSettingsTabs({
  activeKey,
  buildHref = buildTenantRelativePath,
}) {
  const activeMeta = getTabMeta(activeKey);

  // AdminLayout only mounts this component when settingsTabActiveKey is
  // truthy, and that value has always already passed through
  // normalizeAdminSettingsTabKey (or is one of the three hardcoded literal
  // keys for menu-builder/custom-pages/domains->events) — so activeMeta
  // should never actually be null in practice. Kept as a defensive
  // no-render rather than crashing on an unrecognized key.
  if (!activeMeta) {
    return null;
  }

  // Group-scoped sibling strip (prototype: .adm-setsibs / .adm-settab,
  // 06-…jsx:60-64). The prototype only renders this when
  // `meta.siblings.length > 1` — true for all 16 prototype/hub-backed
  // keys here. We render it for the length-1 emailNotifications case too
  // (rather than gate it away entirely): Settings.jsx's shared tabpanel
  // wrapper points `aria-labelledby` at getAdminSettingsTabId(activeTab)
  // for every tab (Settings.jsx:4346, 12378) — hiding the strip for that
  // one key would leave that id unrendered anywhere in the DOM, a
  // dangling aria-labelledby reference. A single-pill strip is a minor,
  // harmless deviation from the prototype (which never had this orphan
  // key to begin with) in exchange for keeping the ARIA contract intact.
  const siblings = getGroupSiblings(activeKey);

  return (
    <>
      <Link
        to={buildHref("/administrator/settings")}
        className={styles.backLink}
        aria-label="Back to all settings"
        data-testid="admin-settings-back-link"
      >
        <i className={`${shell.icon} ${styles.backIcon}`} aria-hidden="true" />
        All settings
      </Link>

      <div className={styles.panelHead} data-testid="admin-settings-panel-head">
        <span className={styles.panelHeadIcon}>
          <i
            className={`${shell.icon} ${shell[activeMeta.icon]}`}
            aria-hidden="true"
          />
        </span>
        <div className={styles.panelHeadText}>
          <div className={styles.panelHeadTitle}>{activeMeta.label}</div>
          <div className={styles.panelHeadDesc}>{activeMeta.description}</div>
        </div>

        <div
          className={styles.setsibs}
          role="tablist"
          aria-label="Admin settings tabs"
          data-testid="admin-settings-tabs"
        >
          {siblings.map((tab) => {
            const isActive = activeKey === tab.key;

            return (
              <Link
                key={tab.key}
                to={getTabHref(tab, buildHref)}
                id={getAdminSettingsTabId(tab.key)}
                role="tab"
                className={`${styles.settab} ${
                  isActive ? styles.settabActive : ""
                }`.trim()}
                aria-current={isActive ? "page" : undefined}
                aria-selected={isActive}
                aria-controls={getAdminSettingsTabPanelId(tab.key)}
                aria-label={`Open ${tab.label} settings`}
                data-testid={`admin-settings-tab-${tab.key}`}
                data-admin-settings-tab={tab.key}
              >
                <span className={styles.tabLabel}>{tab.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
}
